import React, { useState } from 'react';
import { useFinance, api } from '../context/FinanceContext';
import { ChevronLeft, Download, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function Reports() {
  const { months, loading } = useFinance();
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadPDF = async (monthItem) => {
    try {
      setDownloadingId(monthItem.id);
      
      // Fetch full details for the specific month
      const res = await api.get(`/months/${monthItem.id}`);
      const data = res.data;
      
      const { month, summary, budgets, debts } = data;
      
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(22);
      doc.text("KATABO", 105, 20, { align: "center" });
      
      doc.setFontSize(14);
      doc.text("Monthly Financial Report", 105, 30, { align: "center" });
      
      const monthName = new Date(0, month.month - 1).toLocaleString('default', { month: 'long' });
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text(`Period: ${monthName} ${month.year}`, 105, 38, { align: "center" });
      
      // Expected Income
      autoTable(doc, {
        startY: 50,
        head: [['Summary', 'Amount']],
        body: [
          ['Expected Income', `K${summary.expected_income}`],
          ['Rent', `K${month.rent_amount || 0}`]
        ],
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185] }
      });
      
      // Budgets
      if (budgets && budgets.length > 0) {
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 10,
          head: [['Budgeted Items', 'Amount']],
          body: budgets.map(b => [b.name, `K${b.amount}`]),
          theme: 'grid',
          headStyles: { fillColor: [41, 128, 185] }
        });
      }
      
      // Debts
      if (debts && debts.length > 0) {
        autoTable(doc, {
          startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 70,
          head: [['Debts', 'Status', 'Amount']],
          body: debts.map(d => [d.name, d.status, `K${d.amount}`]),
          theme: 'grid',
          headStyles: { fillColor: [41, 128, 185] }
        });
      }
      
      // Final Totals
      autoTable(doc, {
        startY: doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 70,
        head: [['Final Calculations', 'Amount']],
        body: [
          ['Total Deductions (Rent + Budgets + Debts)', `K${summary.committed}`],
          ['Remaining Balance', `K${summary.remaining}`]
        ],
        theme: 'grid',
        headStyles: { fillColor: [39, 174, 96] },
        bodyStyles: { fontStyle: 'bold' }
      });
      
      doc.save(`Katabo_Report_${monthName}_${month.year}.pdf`);
      
    } catch (e) {
      console.error("Failed to generate PDF", e);
      alert(`Failed to generate PDF: ${e.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto pb-24 md:pb-6 space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center gap-4 sticky top-0 bg-gray-50 z-20 py-4 px-4 sm:px-6 lg:px-8 shadow-sm">
        <Link to="/" className="text-gray-500 hover:text-gray-800 transition-colors">
          <ChevronLeft className="w-6 h-6" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">Export Reports</h1>
          <p className="text-sm text-gray-500">Download a simple PDF summary for any month</p>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 space-y-4">
        {loading && months.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : months.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-white rounded-2xl shadow-sm border border-gray-100">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p>No recorded months found.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden divide-y divide-gray-100">
            {months.map(m => {
              const monthName = new Date(0, m.month - 1).toLocaleString('default', { month: 'long' });
              const isDownloading = downloadingId === m.id;
              return (
                <div key={m.id} className="flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-50 text-blue-600 rounded-xl hidden sm:block">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">{monthName} {m.year}</h2>
                      <p className="text-sm text-gray-500 hidden sm:block">Expected Income: K{m.expected_income}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleDownloadPDF(m)}
                    disabled={isDownloading}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {isDownloading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <span className="hidden sm:inline">{isDownloading ? 'Generating...' : 'Download PDF'}</span>
                    <span className="sm:hidden">{isDownloading ? '...' : 'PDF'}</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
